import type { ReactNode } from "react";
import * as RadixDropdownMenu from "@radix-ui/react-dropdown-menu";
import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/cn";
import type { RowActionItem } from "@/components/ui/RowActions";

/**
 * DropdownMenu — the single Radix menu implementation for the whole app.
 *
 * Both `RowActions` (the `⋯` overflow) and `ExportOrdersDropdown` (and every
 * migrated dispatcher surface) render their menu through THIS component, so the
 * panel/item markup (padding, radius, shadow, focus/destructive styling) is
 * identical everywhere.
 *
 * The caller owns the trigger (usually a `<Button asChild>` / `<IconButton>`);
 * it is wrapped in `<Trigger asChild>`. Items reuse the shared `RowActionItem`
 * shape from `RowActions` — do NOT invent a new item type.
 *
 * Two optional structures on top of the flat `items` list (TASK-4706):
 *   - `groups` — labelled, separator-divided sections. Use when a single menu
 *     mixes families of actions that must not be confusable at a glance
 *     (e.g. "Import" vs "Export" in the Orders header overflow).
 *   - `item.items` — turns that entry into a Radix SUBMENU, so an action that
 *     still needs a choice (export → CSV / XLSX) stays one entry in the parent
 *     menu instead of exploding it into a flat list.
 */

/** A labelled, separator-divided section of a menu. */
export interface DropdownMenuGroup {
  /** Stable key for React. */
  key: string;
  /** Already-translated section label; omit for an unlabelled section. */
  label?: string;
  items: RowActionItem[];
}

interface Props {
  /** The element that opens the menu; wrapped in `<Trigger asChild>`. */
  trigger: ReactNode;
  /**
   * Flat menu entries (reuses the `RowActionItem` shape from `RowActions`).
   * Rendered before `groups` when both are supplied.
   */
  items?: RowActionItem[];
  /** Labelled, separator-divided sections. Rendered after `items`. */
  groups?: DropdownMenuGroup[];
  /** Alignment of the content relative to the trigger. Defaults to "end". */
  align?: "start" | "center" | "end";
  /** Side the content opens on. Defaults to "bottom". */
  side?: "top" | "right" | "bottom" | "left";
  /** Extra classes merged onto the menu content panel. */
  menuClassName?: string;
  /**
   * Notified when the menu opens/closes. Prefer this over an `onClick` on the
   * trigger for open-driven side effects (e.g. lazily arming a query): Radix's
   * `Trigger asChild` toggles the menu on pointer events and does not reliably
   * forward the child's `onClick`, so an `onClick` on the trigger can silently
   * never fire.
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * Content rendered at the top of the panel, above `items` and `groups` —
   * e.g. a search input that filters the items (Chat's driver picker,
   * Sprint 222). Additive: no other caller passes it.
   */
  header?: ReactNode;
}

const PANEL_CLASS =
  "z-50 min-w-[160px] rounded-lg border border-gray-200 bg-white p-1 shadow-lg";

const ITEM_CLASS =
  "flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm outline-none focus:bg-gray-50 data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50";

function itemTone(destructive?: boolean) {
  return destructive
    ? "text-brand-danger focus:bg-brand-danger/10"
    : "text-gray-700 hover:bg-gray-50";
}

function MenuIcon({ icon }: { icon: ReactNode }) {
  return (
    <span aria-hidden="true" className="inline-flex">
      {icon}
    </span>
  );
}

/**
 * Renders one entry: a plain item, or — when `item.items` is present — a
 * submenu whose children reuse the exact same item markup.
 */
function MenuEntry({ item }: { item: RowActionItem }) {
  if (item.items && item.items.length > 0) {
    return (
      <RadixDropdownMenu.Sub>
        <RadixDropdownMenu.SubTrigger
          disabled={item.disabled}
          className={cn(ITEM_CLASS, itemTone(item.destructive))}
        >
          {item.icon && <MenuIcon icon={item.icon} />}
          <span className="flex-1">{item.label}</span>
          <ChevronRight size={14} aria-hidden="true" />
        </RadixDropdownMenu.SubTrigger>
        <RadixDropdownMenu.Portal>
          <RadixDropdownMenu.SubContent
            sideOffset={4}
            className={cn(PANEL_CLASS)}
          >
            {item.items.map((child) => (
              <MenuEntry key={child.key} item={child} />
            ))}
          </RadixDropdownMenu.SubContent>
        </RadixDropdownMenu.Portal>
      </RadixDropdownMenu.Sub>
    );
  }

  return (
    <RadixDropdownMenu.Item
      disabled={item.disabled}
      onSelect={item.onSelect}
      title={item.hint}
      className={cn(ITEM_CLASS, itemTone(item.destructive))}
    >
      {item.icon && <MenuIcon icon={item.icon} />}
      {item.hint ? (
        <span className="flex flex-col">
          <span>{item.label}</span>
          <span className="text-xs text-gray-400">{item.hint}</span>
        </span>
      ) : (
        item.label
      )}
    </RadixDropdownMenu.Item>
  );
}

export function DropdownMenu({
  trigger,
  items = [],
  groups = [],
  align = "end",
  side = "bottom",
  menuClassName,
  onOpenChange,
  header,
}: Props) {
  return (
    <RadixDropdownMenu.Root onOpenChange={onOpenChange}>
      <RadixDropdownMenu.Trigger asChild>{trigger}</RadixDropdownMenu.Trigger>
      <RadixDropdownMenu.Portal>
        <RadixDropdownMenu.Content
          align={align}
          side={side}
          sideOffset={4}
          className={cn(PANEL_CLASS, menuClassName)}
        >
          {header}
          {items.map((item) => (
            <MenuEntry key={item.key} item={item} />
          ))}

          {groups.map((group, index) => (
            <RadixDropdownMenu.Group key={group.key}>
              {/* A divider precedes every section except the first thing in
                  the panel, so the families read as distinct blocks. */}
              {(index > 0 || items.length > 0) && (
                <RadixDropdownMenu.Separator className="my-1 h-px bg-gray-100" />
              )}
              {group.label && (
                <RadixDropdownMenu.Label className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-gray-400">
                  {group.label}
                </RadixDropdownMenu.Label>
              )}
              {group.items.map((item) => (
                <MenuEntry key={item.key} item={item} />
              ))}
            </RadixDropdownMenu.Group>
          ))}
        </RadixDropdownMenu.Content>
      </RadixDropdownMenu.Portal>
    </RadixDropdownMenu.Root>
  );
}
